import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useListing } from '@/hooks/useListing';
import { useAuth } from '@/hooks/useAuth';
import { Listing } from '@/types/listing';
import { apiClient } from '@/lib/api-client';
import { listingsAPI } from '@/lib/api/listings';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import SellerReviews from '@/components/listings/SellerReviews';
import {
  ArrowLeft,
  Heart,
  Share2,
  MapPin,
  Clock,
  ShieldCheck,
  Flag,
  MessageCircle,
  Phone,
  Star,
  CheckCircle2,
  X,
} from 'lucide-react';
import { ListingCard, type Listing as VisualListing } from '@/components/site/ListingCard';
import { formatCategory } from '@/lib/utils';
import SEO from '@/components/common/SEO';
import {
  AdsterraBanner320x50,
  AdsterraNativeBanner,
  AdsterraSmartlinkPromo,
} from '@/components/ads';

function timeAgo(dateString: string) {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (isNaN(seconds) || seconds < 0) return 'some time ago';
  let interval = Math.floor(seconds / 31536000);
  if (interval >= 1) return `${interval} yr${interval === 1 ? '' : 's'} ago`;
  interval = Math.floor(seconds / 2592000);
  if (interval >= 1) return `${interval} mo${interval === 1 ? '' : 's'} ago`;
  interval = Math.floor(seconds / 86400);
  if (interval >= 1) return `${interval} day${interval === 1 ? '' : 's'} ago`;
  interval = Math.floor(seconds / 3600);
  if (interval >= 1) return `${interval} hr${interval === 1 ? '' : 's'} ago`;
  interval = Math.floor(seconds / 60);
  if (interval >= 1) return `${interval} min${interval === 1 ? '' : 's'} ago`;
  return 'just now';
}

const getCategoryPlaceholder = (category?: string, id?: string) => {
  const seeds: Record<string, string> = {
    CARS: 'cars-listing', BIKES: 'bikes-listing', MOBILES: 'mobile-listing',
    REAL_ESTATE: 'real-estate-listing', RENT_ROOMMATES: 'rent-roommates-listing',
    BUS_TRUCKS_AUTOS_VEHICLES: 'bus-truck-auto-listing', AUTOMOBILE_ACCESSORIES: 'auto-accessories',
    PETS_AND_HOBBIES: 'pets-hobbies', COMMUNITY_AND_EVENTS: 'community-events',
    FURNITURE_INTERIORS: 'furniture-interiors', ELECTRONICS_AND_APPLIANCES: 'electronics-appliances',
    PERSONAL_AND_COMMUNITY: 'personal-community', JOBS_AND_SERVICES: 'jobs-services',
    FASHION_AND_LIFESTYLE: 'fashion-lifestyle', SAFETY_PROTECTION: 'safety-protection',
    BUSINESS_FINANCIAL: 'business-financial', PROFESSIONAL_SERVICES: 'professional-services',
    INSURANCE_LEGAL: 'insurance-legal', MAINTENANCE_AND_REPAIR: 'maintenance-repair',
    PERSONAL_SERVICES: 'personal-services', SALES_AUCTIONS: 'sales-auctions',
    GIFTS_SPECIAL_OCCASIONS: 'gifts-occasions', TRAVEL_ENTERTAINMENT: 'travel-entertainment',
    WATER_FILTRATION: 'water-filtration', SECURITY_GUARDS: 'security-guards',
    OTHER_SERVICES: 'other-services',
  };
  const seed = seeds[category?.toUpperCase() || ''] || id || 'default';
  return `https://picsum.photos/seed/${seed}/600/450`;
};

function toVisualListing(api: Listing): VisualListing {
  return {
    id: api.id,
    image: api.images?.[0] || getCategoryPlaceholder(api.category, api.id),
    title: api.title,
    price: `₹${api.price.toLocaleString('en-IN')}`,
    location: api.location,
    postedAt: timeAgo(api.createdAt),
    featured: api.price > 50000,
    description: api.description,
  };
}

interface SellerProfile {
  id: string;
  userId: string;
  businessName?: string;
  bio?: string;
  phone?: string;
  rating: number;
  reviewCount: number;
  totalListings: number;
  totalSales: number;
  verified: boolean;
  status: string;
  responseTimeMinutes?: number;
  businessType?: string;
}

export const ListingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getById, getAll, loading, error } = useListing();
  const { isAuthenticated, user } = useAuth();

  const [listing, setListing] = useState<Listing | null>(null);
  const [similar, setSimilar] = useState<Listing[]>([]);
  const [recommendations, setRecommendations] = useState<Listing[]>([]);
  const [activeImage, setActiveImage] = useState(0);
  const [phoneRevealed, setPhoneRevealed] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [seller, setSeller] = useState<SellerProfile | null>(null);
  const [mapCoords, setMapCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [originalName, setOriginalName] = useState<string | null>(null);
  const [sellerUserPhone, setSellerUserPhone] = useState<string | null>(null);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [isLiked, setIsLiked] = useState(false);

  const handleToggleLike = async () => {
    if (!listing) return;
    try {
      const res = await listingsAPI.toggleLike(listing.id, listing);
      setIsLiked(res.liked);
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  };

  useEffect(() => {
    if (id) {
      loadListingData(id);
    }
  }, [id]);

  useEffect(() => {
    if (listing?.id) {
      fetchRecommendations();
    }
  }, [listing?.id]);

  const fetchRecommendations = async () => {
    try {
      const userId = user?.sub || '00000000-0000-0000-0000-000000000000';
      const response = await apiClient.get(`/api/search/recommendations?userId=${userId}`);
      
      const mappedRecommendations = (response || []).map((item: any) => ({
        ...item,
        price: item.salaryMin || 0,
        createdAt: item.createdAt || new Date().toISOString(),
        images: item.images || []
      }));
      
      setRecommendations(mappedRecommendations);
    } catch (err) {
      console.error('Failed to fetch recommendations:', err);
    }
  };

  // Geocode location for map
  useEffect(() => {
    if (!listing?.location) return;
    const controller = new AbortController();
    setMapLoading(true);
    setMapCoords(null);

    const parts = listing.location.split(',').map(p => p.trim());
    const queryLocation = parts.length >= 2
      ? `${parts[parts.length - 2]}, ${parts[parts.length - 1]}`
      : listing.location;

    fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(queryLocation + ', India')}&limit=1`,
      { 
        headers: { 'Accept-Language': 'en' },
        signal: controller.signal
      }
    )
      .then((res) => res.json())
      .then((data) => {
        if (data && data.length > 0) {
          setMapCoords({ lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) });
        }
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setMapLoading(false);
        }
      });

    return () => controller.abort();
  }, [listing?.location]);

  const loadListingData = async (listingId: string) => {
    try {
      const data = await getById(listingId);
      if (!data) {
        setListing(null);
        return;
      }
      setListing(data);
      setIsLiked(listingsAPI.isLiked(listingId));
      setActiveImage(0);
      setPhoneRevealed(false);
      setOriginalName(null);
      setSellerUserPhone(null);
      setShowPhoneModal(false);

      // Fetch seller profile using sellerId from the listing
      if (data.sellerId) {
        try {
          const sellerData = await apiClient.get(`/api/sellers/${data.sellerId}`);
          setSeller(sellerData);
          if (sellerData?.userId) {
            try {
              const userData = await apiClient.get(`/api/users/public/${sellerData.userId}`);
              if (userData?.name) {
                setOriginalName(userData.name);
              }
              if (userData?.phone) {
                setSellerUserPhone(userData.phone);
              }
            } catch (userErr) {
              console.warn('Could not fetch user details:', userErr);
            }
          }
        } catch (sellerErr) {
          console.warn('Could not fetch seller profile:', sellerErr);
          setSeller(null);
        }
      }

      // Fetch similar listings
      if (data.category) {
        const similarData = await getAll({
          category: data.category.toLowerCase(),
          limit: 4,
        });
        // Spring Data Page returns { content: [...] }; fall back to .items or plain array
        const similarItems = similarData?.content ?? similarData?.items ?? (Array.isArray(similarData) ? similarData : []);
        setSimilar(similarItems.filter((item: Listing) => item.id !== listingId));
      }
    } catch (err) {
      console.error('Failed to load listing details:', err);
    }
  };

  const maskPhone = (phone: string) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length <= 4) return phone;
    // Show first 2 and last 2 digits, mask the rest
    return digits.slice(0, 2) + '*'.repeat(digits.length - 4) + digits.slice(-2);
  };

  const getSellerName = () => {
    if (seller?.businessName) return seller.businessName;
    if (originalName) return originalName;
    // Extract name from bio (format: "Seller profile for <name>")
    if (seller?.bio) {
      const bioMatch = seller.bio.match(/^Seller profile for (.+)$/i);
      if (bioMatch?.[1]) return bioMatch[1];
    }
    return listing?.sellerName || 'Private Seller';
  };

  const getSellerPhone = () => {
    if (listing?.sellerPhone) return listing.sellerPhone;

    let phone = seller?.phone;
    const isDummy = (p: string) => {
      const digits = p.replace(/\D/g, '');
      return !digits || digits.length < 6 || /^(\d)\1+$/.test(digits);
    };

    if (!phone || isDummy(phone)) {
      phone = sellerUserPhone || undefined;
    }

    if (!phone || isDummy(phone)) return null;
    return phone;
  };

  const getSellerRating = () => {
    if (seller?.rating !== undefined && seller?.rating !== null) return seller.rating;
    if (listing?.sellerRating) return listing.sellerRating;
    return 0;
  };

  const handleStartChat = async (customMessage?: string) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/listings/${id}` } });
      return;
    }
    if (!listing) return;

    setChatLoading(true);
    try {
      // Create a conversation via inquiry-service
      const conversation = await apiClient.post('/api/messages/conversations', {
        recipientId: listing.sellerId,
        listingId: listing.id,
        listingSellerId: listing.sellerId,
        listingTitle: listing.title,
        listingPrice: listing.price,
        initialMessage: customMessage || `Hi, I'm interested in "${listing.title}"`,
      });
      // Navigate to messages with the new conversation selected
      navigate(`/messages?conversationId=${conversation.id}`);
    } catch (err: any) {
      console.error('Failed to create conversation:', err);
      // If conversation already exists, still navigate to messages
      navigate(`/messages?listingId=${id}`);
    } finally {
      setChatLoading(false);
    }
  };

  const images = useMemo(() => 
    listing?.images && listing.images.length > 0 ? listing.images : [getCategoryPlaceholder(listing?.category, listing?.id)],
    [listing?.images, listing?.category, listing?.id]
  );
  const productJsonLd = useMemo(() => listing ? ({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: listing.title,
    description: listing.description || `${listing.title} for sale on MemAds`,
    image: images[0],
    category: formatCategory(listing.category),
    offers: {
      '@type': 'Offer',
      price: listing.price,
      priceCurrency: 'INR',
      availability: listing.status === 'SOLD' ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
      itemCondition: listing.condition === 'NEW' ? 'https://schema.org/NewCondition' : 'https://schema.org/UsedCondition',
    },
  }) : null, [listing?.id, listing?.title, listing?.description, images, listing?.category, listing?.price, listing?.status, listing?.condition]);

  if (loading && !listing) {
    return <LoadingSpinner />;
  }

  if (error || !listing) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-16 text-center">
        <h2 className="text-2xl font-bold text-gray-900">Listing Not Found</h2>
        <p className="mt-2 text-gray-600">The listing you are looking for does not exist or has been removed.</p>
        <Link to="/listings" className="mt-6 inline-flex items-center gap-2 text-primary font-semibold hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to listings
        </Link>
      </div>
    );
  }

  const formattedPrice = `₹${listing.price.toLocaleString('en-IN')}`;
  const sellerName = getSellerName();
  const sellerPhone = getSellerPhone();
  const sellerRating = getSellerRating();
  const isVerified = seller?.verified ?? false;

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 bg-background">
      <SEO
        title={`${listing.title} — ${formattedPrice}`}
        description={listing.description?.slice(0, 160) || `${listing.title} for sale at ${formattedPrice} on MemAds. ${formatCategory(listing.category)} in ${listing.location}.`}
        canonical={`/listings/${listing.id}`}
        ogImage={images[0]}
        ogType="product"
        jsonLd={productJsonLd}
      />
      <nav className="mb-4 flex items-center gap-1 text-sm text-muted-foreground">
        <Link to="/listings" className="inline-flex items-center gap-1 hover:text-primary transition">
          <ArrowLeft className="h-4 w-4" /> Back to listings
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground/70">{formatCategory(listing.category)}</span>
      </nav>

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        {/* Main Details Body */}
        <div className="space-y-6">
          {/* Image Gallery */}
          <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
            <div className="relative aspect-[4/3] bg-secondary">
              <img
                src={images[activeImage]}
                alt={listing.title}
                className="h-full w-full object-cover"
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  if (!img.dataset.retried) {
                    img.dataset.retried = 'true';
                    img.src = getCategoryPlaceholder(listing.category, listing.id);
                  }
                }}
              />
              {listing.price > 50000 && (
                <span className="absolute left-4 top-4 rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wide text-accent-foreground shadow">
                  Featured
                </span>
              )}
              <div className="absolute right-4 top-4 flex gap-2">
                <button
                  onClick={handleToggleLike}
                  aria-label={isLiked ? "Remove from saved" : "Save"}
                  className="grid h-10 w-10 place-items-center rounded-full bg-background/90 text-foreground backdrop-blur shadow hover:bg-accent hover:text-accent-foreground transition"
                >
                  <Heart className={`h-4 w-4 ${isLiked ? 'fill-red-500 text-red-500' : ''}`} />
                </button>
                <button
                  aria-label="Share"
                  onClick={async () => {
                    const shareData = {
                      title: listing?.title || 'Check out this listing on MemAds',
                      text: listing?.description?.slice(0, 100) || 'Check out this listing on MemAds',
                      url: window.location.href,
                    };
                    try {
                      if (navigator.share) {
                        await navigator.share(shareData);
                      } else {
                        await navigator.clipboard.writeText(window.location.href);
                        alert('Link copied to clipboard!');
                      }
                    } catch (err) {
                      // User cancelled share or clipboard failed — try fallback
                      try {
                        await navigator.clipboard.writeText(window.location.href);
                        alert('Link copied to clipboard!');
                      } catch {
                        // ignore
                      }
                    }
                  }}
                  className="grid h-10 w-10 place-items-center rounded-full bg-background/90 text-foreground backdrop-blur shadow hover:bg-primary hover:text-primary-foreground transition"
                >
                  <Share2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto p-3">
                {images.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    className={`h-20 w-28 shrink-0 overflow-hidden rounded-lg border-2 transition ${
                      activeImage === i ? "border-primary" : "border-transparent opacity-70 hover:opacity-100"
                    }`}
                  >
                    <img src={src} alt={`Photo ${i + 1}`} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Pricing & Metadata Section */}
          <section className="rounded-2xl border border-border bg-card p-5 shadow-card">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-heading text-3xl font-bold text-foreground">{formattedPrice}</div>
                <h1 className="mt-1 font-heading text-xl font-semibold text-foreground md:text-2xl">{listing.title}</h1>
              </div>
              <span className="rounded-full bg-primary-soft px-3 py-1 text-xs font-semibold text-primary">{formatCategory(listing.category)}</span>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {listing.location}</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> Posted {timeAgo(listing.createdAt)}</span>
              {isVerified && (
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-primary" /> Verified Seller</span>
              )}
            </div>
          </section>

          {/* Description Section */}
          <section className="rounded-2xl border border-border bg-card p-5 shadow-card">
            <h2 className="font-heading text-lg font-bold text-foreground">Description</h2>
            <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-foreground/80">
              {listing.description || "No description provided by the seller."}
            </p>
            <dl className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-border bg-background p-3">
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Condition</dt>
                <dd className="mt-0.5 text-sm font-medium text-foreground capitalize">{listing.condition?.toLowerCase()?.replace('_', ' ') || 'Good'}</dd>
              </div>
              <div className="rounded-xl border border-border bg-background p-3">
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Category</dt>
                <dd className="mt-0.5 text-sm font-medium text-foreground">{formatCategory(listing.category)}</dd>
              </div>
              <div className="rounded-xl border border-border bg-background p-3">
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Location</dt>
                <dd className="mt-0.5 text-sm font-medium text-foreground">{listing.location}</dd>
              </div>
            </dl>
          </section>

          {/* Safety Tips Section */}
          <section className="rounded-2xl border border-border bg-primary-soft/40 p-5">
            <h3 className="inline-flex items-center gap-2 font-heading text-base font-bold text-foreground">
              <ShieldCheck className="h-5 w-5 text-primary" /> Stay safe on MemAds
            </h3>
            <ul className="mt-3 grid gap-2 text-sm text-foreground/80 sm:grid-cols-2">
              <li className="inline-flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Meet sellers in public places.</li>
              <li className="inline-flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Inspect the item before paying.</li>
              <li className="inline-flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Never share OTPs or bank details.</li>
              <li className="inline-flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Report suspicious listings.</li>
            </ul>
            <button className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-foreground/70 hover:text-primary transition">
              <Flag className="h-3.5 w-3.5" /> Report this ad
            </button>
          </section>
        </div>

        {/* Sidebar Actions */}
        <aside className="space-y-4 lg:sticky lg:top-32 lg:self-start">
          <section className="rounded-2xl border border-border bg-card p-5 shadow-card">
            <div className="flex items-center gap-3">
              <div className="grid h-14 w-14 place-items-center rounded-full bg-gradient-primary font-heading text-xl font-bold text-primary-foreground shadow">
                {sellerName?.[0]?.toUpperCase() || 'S'}
              </div>
              <div className="min-w-0">
                <div className="font-heading text-base font-bold text-foreground">{sellerName}</div>
                <div className="mt-0.5 inline-flex items-center gap-1 text-xs text-muted-foreground">
                  {isVerified ? (
                    <><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Verified Profile</>
                  ) : (
                    <span>Seller Profile</span>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1 text-foreground">
                <Star className="h-3.5 w-3.5 fill-accent text-accent" /> {sellerRating > 0 ? sellerRating.toFixed(1) : 'New'}
              </span>
              {seller?.reviewCount !== undefined && seller.reviewCount > 0 && (
                <>
                  <span>·</span>
                  <span>{seller.reviewCount} review{seller.reviewCount !== 1 ? 's' : ''}</span>
                </>
              )}
            </div>
          </section>

          <section className="space-y-3 rounded-2xl border border-border bg-card p-5 shadow-card">
            <button
              onClick={() => handleStartChat()}
              disabled={chatLoading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-elevated transition hover:brightness-110 disabled:opacity-60"
            >
              <MessageCircle className="h-4 w-4" />
              {chatLoading ? "Opening chat..." : "Chat with seller"}
            </button>

            {sellerPhone ? (
              <>
                <button
                  type="button"
                  onClick={() => setShowPhoneModal(true)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 py-3 text-sm font-semibold text-foreground transition hover:border-primary hover:text-primary"
                >
                  <Phone className="h-4 w-4" />
                  {maskPhone(sellerPhone)}
                </button>

                <a
                  href={`https://wa.me/${sellerPhone.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-3 text-sm font-semibold text-white shadow-elevated transition hover:brightness-110"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp Seller
                </a>
              </>
            ) : (
              <button
                disabled
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 py-3 text-sm font-semibold text-muted-foreground opacity-60"
              >
                <Phone className="h-4 w-4" />
                Phone not available
              </button>
            )}
            <p className="text-center text-xs text-muted-foreground">
              Never pay advance. Inspect the item first.
            </p>
          </section>

          <section className="rounded-2xl border border-border bg-card p-5 shadow-card">
            <h3 className="font-heading text-sm font-bold text-foreground">Location</h3>
            <p className="mt-1 text-sm text-foreground/80">{listing.location}</p>
            <div className="mt-3 h-44 rounded-xl overflow-hidden border border-border relative">
              {mapLoading ? (
                <div className="h-full flex items-center justify-center bg-secondary text-muted-foreground text-sm">
                  <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                  Loading map...
                </div>
              ) : mapCoords ? (
                <iframe
                  title="Listing location map"
                  className="absolute top-0 left-0 w-full h-[calc(100%+40px)]"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${mapCoords.lon - 0.05}%2C${mapCoords.lat - 0.03}%2C${mapCoords.lon + 0.05}%2C${mapCoords.lat + 0.03}&layer=mapnik&marker=${mapCoords.lat}%2C${mapCoords.lon}`}
                />
              ) : (
                <div className="h-full flex items-center justify-center bg-secondary text-muted-foreground text-sm">
                  <MapPin className="h-5 w-5 mr-1" /> Map not available
                </div>
              )}
            </div>
          </section>

          {/* Seller Reviews */}
          {listing.sellerId && (
            <SellerReviews
              sellerId={listing.sellerId}
              listingId={listing.id}
              listingTitle={listing.title}
            />
          )}

          {/* Adsterra Sponsored Sidebar Units */}
          <div className="pt-2 flex flex-col items-center">
            <AdsterraBanner320x50 />
            <AdsterraSmartlinkPromo variant="card" className="w-full mt-3" />
          </div>
        </aside>
      </div>

      {/* Adsterra Native Banner */}
      <div className="mt-8">
        <AdsterraNativeBanner />
      </div>

      {/* Similar Listings Section */}
      {similar.length > 0 && (
        <section className="mt-12">
          <div className="mb-5 flex items-end justify-between">
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground">Similar listings</h2>
              <p className="mt-1 text-sm text-muted-foreground">More ads in {formatCategory(listing.category)}.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {similar.map((item) => (
              <ListingCard key={item.id} listing={toVisualListing(item)} />
            ))}
          </div>
        </section>
      )}

      {/* Recommendations Section */}
      {recommendations.length > 0 && (
        <section className="mt-12">
          <div className="mb-5 flex items-end justify-between">
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground">You May Also Like</h2>
              <p className="mt-1 text-sm text-muted-foreground">Based on your interests.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {recommendations.map((item) => (
              <ListingCard key={item.id} listing={toVisualListing(item)} />
            ))}
          </div>
        </section>
      )}

      {showPhoneModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-fade-in">
          <div className="relative w-full max-w-sm rounded-2xl border border-border bg-card p-6 shadow-elevated animate-scale-in">
            <button
              onClick={() => setShowPhoneModal(false)}
              className="absolute right-4 top-4 text-muted-foreground hover:text-foreground transition"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 rounded-full bg-primary/10 p-3 text-primary">
                <Phone className="h-6 w-6" />
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground">Phone Number Hidden</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                To protect privacy, the seller's full phone number is not displayed. You can request it directly via chat.
              </p>
              <div className="mt-6 flex w-full flex-col gap-2">
                <button
                  onClick={() => {
                    setShowPhoneModal(false);
                    handleStartChat(`Hi, I'm interested in your listing "${listing?.title}". Could you please share your phone number?`);
                  }}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-elevated transition hover:brightness-110"
                >
                  <MessageCircle className="h-4 w-4" />
                  Chat with seller to ask phone number
                </button>
                <button
                  onClick={() => setShowPhoneModal(false)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 py-3 text-sm font-semibold text-foreground transition hover:bg-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default ListingDetailPage;
